import React from 'react';
import _ from 'lodash';
import { daysAgo } from '../../common/SharedFunctions';
import { Accordion, SortableHeader } from '../../common/SharedComponents';
import EditBrokerClientModal from '../../brokerClients/modals/EditBrokerClientModal';
import { RefreshEvents } from '../../common/SharedEvents';
import store, { brokerActions, brokerClientActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { BrokerApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { faPlus, faFlag } from '@fortawesome/free-solid-svg-icons';

const columns = [
    { key: 'Status', label: 'Status', id: 'calculatedStatus' },
    { key: 'Flgd', label: 'Flag', id: 'isFlagged' },
    { key: 'Broker', label: 'Broker' },
    { key: 'Broker Accepted', label: 'Broker Accepted' },
    { key: 'Point of Contact', label: 'Point of Contact' },
    { key: 'Company', label: 'Company', id: 'businessName' },
    { key: 'ICCA', label: 'ICCA' },
    {
        key: 'Last Added',
        label: 'Last Added',
        id: 'updatedAt',
        className: 'secondary count',
    },
    {
        key: 'Pending',
        label: 'Pending',
        id: 'totalPending',
        className: 'secondary count',
    },
    {
        key: 'Initial Expiry',
        label: 'Initial Expiry',
        id: 'totalInitialExpiry',
        className: 'secondary count',
    },
    {
        key: 'Active',
        label: 'Active',
        id: 'totalActive',
        className: 'secondary count',
    },
    {
        key: 'Rejected',
        label: 'Rejected',
        id: 'totalRejected',
        className: 'secondary',
    },
    {
        key: 'Expired',
        label: 'Expired',
        id: 'totalExpired',
        className: 'secondary',
    },
];

const daysAgoColumnStyle = {
    width: '110px',
};

const ActiveBrokerTable = () => {
    const searchState = useSelector((state) => state.searchState);
    const brokerApi = new BrokerApi.BrokerApi(applicationInsights);

    const { dispatch } = store;

    const refreshEvents = new RefreshEvents();

    const _handleRowClick = (row) => {
        dispatch(brokerActions.setBrokerId({ brokerId: row.id }));
        dispatch(brokerActions.setBrokerModalOpen({ openBrokerModal: true }));
    };

    const handleAddBrokerClient = ({ id }) => {
        dispatch(brokerClientActions.setBrokerClientId({ brokerClientId: null }));
        dispatch(brokerClientActions.setBrokerClientBrokerId({ brokerId: id }));
        dispatch(
            brokerClientActions.setBrokerClientOpen({
                openBrokerClientModal: true,
            })
        );
    };

    const _loadBrokers = async () => {
        const response = await brokerApi.getActiveBrokerClientSummary();

        if (response.statusCode !== 200) return;

        set_rows(
            response.result.map((s) => ({
                ...s,
                name: s.lastName + ', ' + s.firstName,
                updatedDaysAgo: daysAgo(new Date(s.updatedAt)),
            }))
        );
    };

    const [rows, set_rows] = React.useState([]);
    const [sort, set_sort] = React.useState({
        column: 'calculatedStatus',
        direction: 'asc',
    });
    const [filter, set_filter] = React.useState(null);

    const totalCount = rows.length;
    const pendingCount = rows.filter((r) => r.totalPending > 0).length;

    const matchName = (s) => s.name && s.name.toLowerCase().includes(searchState.searchText.broker.toLowerCase());
    const matchBusinessName = (s) =>
        s.businessName && s.businessName.toLowerCase().includes(searchState.searchText.broker.toLowerCase());
    const matchEmail = (s) =>
        s.emailAddress && s.emailAddress.toLowerCase().includes(searchState.searchText.broker.toLowerCase());

    const sortedRows = _.chain(rows)
        .filter((r) => filter === null || (filter === 'pending' && r.totalPending > 0))
        .filter((s) => !searchState.searchText.broker || matchName(s) || matchBusinessName(s) || matchEmail(s))
        .orderBy(sort.column, sort.direction)
        .value();

    React.useEffect(() => {
        _loadBrokers();

        refreshEvents.addRefreshListener(_loadBrokers);
    }, []);

    return (
        <>
            <Accordion title='Activated by BFI, Can Add B-Clients'>
                <div className='button-group'>
                    <button
                        onClick={() => set_filter(null)}
                        className={filter === null ? 'active' : null}
                    >
                        <strong>{totalCount}</strong> All
                    </button>
                    <button
                        onClick={() => set_filter('pending')}
                        className={filter === 'pending' ? 'active' : null}
                    >
                        <strong>{pendingCount}</strong> Brokers w/ Pending B-Client, waiting on BFI
                    </button>
                </div>
                <div className='card'>
                    <div className='card-section'>
                        <table>
                            <thead>
                                <tr className='span-header'>
                                    <th colSpan='8' />
                                    <th
                                        colSpan='6'
                                        className='secondary'
                                    >
                                        <span className='rule'>
                                            <span className='th'>B-clients</span>
                                        </span>
                                    </th>
                                    <th className='secondary' />
                                </tr>
                                <tr>
                                    <th className='count' />
                                    {columns.map((column) => (
                                        <SortableHeader
                                            key={column.key}
                                            column={column}
                                            sort={sort}
                                            onPress={set_sort}
                                        />
                                    ))}
                                    <th className='secondary'></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((row) => (
                                    <tr key={row.id}>
                                        <td
                                            className='count'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            {row.totalPending > 0 && (
                                                <span className='reddot'>
                                                    <span className='sr-only'>Needs attention</span>
                                                </span>
                                            )}
                                        </td>
                                        <td
                                            className='status'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            {row.totalPending > 0 && (
                                                <>
                                                    <FontAwesomeIcon
                                                        icon={faClock}
                                                        className='icon'
                                                    />{' '}
                                                    Pending
                                                    <div>B-client</div>
                                                </>
                                            )}
                                        </td>
                                        <td
                                            className='isFlagged'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            {row.isFlagged && (
                                                <i
                                                    style={{ color: 'darkred' }}
                                                    title='Broker is Flagged!'
                                                >
                                                    <FontAwesomeIcon icon={faFlag} />
                                                </i>
                                            )}
                                        </td>
                                        <td
                                            className='primary'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            {row.name}
                                        </td>
                                        <td onClick={() => _handleRowClick(row)}>{row.accepted ? 'Yes' : 'No'}</td>
                                        <td onClick={() => _handleRowClick(row)}>{row.pointOfContact}</td>
                                        <td onClick={() => _handleRowClick(row)}>{row.businessName}</td>
                                        <td onClick={() => _handleRowClick(row)}>{row.icca}</td>
                                        <td
                                            className='secondary time-stamp'
                                            style={daysAgoColumnStyle}
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            {row.updatedDaysAgo}
                                        </td>
                                        <td
                                            className='secondary  count notice'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            <span className={row.totalPending === 0 ? 'zero' : null}>{row.totalPending}</span>
                                        </td>
                                        <td
                                            className='secondary count negative'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            <span className={row.totalInitialExpiry === 0 ? 'zero' : null}>
                                                {row.totalInitialExpiry}
                                            </span>
                                        </td>
                                        <td
                                            className='secondary count positive'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            <span className={row.totalActive === 0 ? 'zero' : null}>{row.totalActive}</span>
                                        </td>
                                        <td
                                            className='secondary count negative'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            <span className={row.totalRejected === 0 ? 'zero' : null}>{row.totalRejected}</span>
                                        </td>
                                        <td
                                            className='secondary count negative'
                                            onClick={() => _handleRowClick(row)}
                                        >
                                            <span className={row.totalExpired === 0 ? 'zero' : null}>{row.totalExpired}</span>
                                        </td>
                                        <td className='secondary actions'>
                                            <button
                                                className='gridButton'
                                                onClick={() => handleAddBrokerClient(row)}
                                            >
                                                <FontAwesomeIcon icon={faPlus} /> Add B-Client
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Accordion>
            <EditBrokerClientModal />
        </>
    );
};

export default ActiveBrokerTable;
